import { createAsyncThunk } from "@reduxjs/toolkit";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";

export interface Item {
  id?: number;
  packageName: string;
  districtCode: string;
  districtName: string;
  state: string;
  status: boolean;
  createBy?: string;
  updatedBy?: string;
  createDate?: string;
  updateDate?: string;
}
export interface PaginatedResponse<T> {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}
export const fetchItemList = createAsyncThunk(
  "item/fetchItemList",
  async ({
    page,
    pageSize,
    search,
    sort,
    order,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: ColumnFiltersState;
  }) => {
    try {
      const params: Record<string, any> = {
        page,
        page_size: pageSize,
      };

      if (search) params.search = search;
      if (sort) params.sort = sort;
      if (order) params.order = order;

      if (filters && Array.isArray(filters)) {
        filters.forEach((filter) => {
          if (filter.id && filter.value !== undefined && filter.value !== "") {
            params[filter.id] = filter.value;
          }
        });
      }

      const response: any = await Axios.get(`/item/?`, { params });
      const responseData = response.data;
      return {
        data: responseData.data || responseData.results || [],
        rowCount: responseData.count || 0,
      };
    } catch (error: any) {
      console.error(
        "fetchItemList error:",
        error.response?.data || error.message
      );
      throw new Error("Failed to fetch item list");
    }
  }
);

export const fetchItem = createAsyncThunk(
  "item/fetchItem",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: ColumnFiltersState;
  }) => {
    try {
      const params: Record<string, any> = {
        page,
        page_size: pageSize,
      };

      if (search) params.search = search;
      if (sort) params.sort = sort;
      if (order) params.order = order;

      if (sort) {
        params.ordering = order === "desc" ? `-${sort}` : sort;
      }

      if (filters && Array.isArray(filters)) {
        filters.forEach((filter) => {
          if (filter.id && filter.value !== undefined && filter.value !== "") {
            params[filter.id] = filter.value;
          }
        });
      }

      const response: any = await Axios.get(`/item/?`, { params });

      const responseData = response.data;
      return {
        data: responseData.data || responseData.results || [],
        rowCount: responseData.count || 0,
      };
    } catch (error: any) {
      console.error("fetchItem error:", error.response?.data || error.message);
      throw new Error("Failed to fetch Items");
    }
  }
);

export const fetchItemsById = createAsyncThunk(
  "item/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/items${id}/`);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch fetch Items By Id"
      );
    }
  }
);

export const updateItem = createAsyncThunk(
  `/organisation/updateItem`,
  async (packageData: any) => {
    const response: any = await Axios.put(
      `/item/${packageData.id}/`,
      packageData
    );

    return response;
  }
);
export const updateStatusInItem = createAsyncThunk(
  `/item/updateStatusInItem`,
  async (itemsData: any) => {
    const response: any = await Axios.put(
      `/items/${itemsData?.item_id}/update/`,
      itemsData
    );
    return response;
  }
);

export const addItem = createAsyncThunk(
  `/item/addItem`,
  async (packageData: any) => {
    const response = await Axios.post(`/organisation/`, packageData);
    return response.data;
  }
);

// export const deleteOrganisation = createAsyncThunk(
//   `/item/delete`,
//   async (id: number, { rejectWithValue }) => {
//     try {
//       await axios.delete(`${API_URL}/${id}`, { headers });
//       return id;
//     } catch (err: any) {
//       console.error("Delete error:", err);
//       return rejectWithValue(err.response?.data?.message || err.message);
//     }
//   }
// );

export const exportItemsCSV = createAsyncThunk(
  "items/exportOItemsCSV",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("authToken");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "*/*",
      };
      const response = await Axios.get("/items/?export=csv/", {
        headers,
        responseType: "blob",
      });
      return {
        blob: response.data,
        filename:
          response.headers["content-disposition"]
            ?.split("filename=")[1]
            ?.replace(/"/g, "") || "items.csv",
      };
    } catch (err: any) {
      console.error("Export CSV error:", err);
      return rejectWithValue(err.message || "Failed to export CSV");
    }
  }
);

export const fetchAllDropdowns = createAsyncThunk(
  "item/fetchAllDropdowns",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/item/dropdown/`);

      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch package list"
      );
    }
  }
);

// export const fetchAllDropdownsItems = createAsyncThunk(
//   "items/fetchAllDropdownsItems",
//   async () => {
//     try {
//       const [orgRes, itemsRes, departmentRes] = await Promise.all([
//         Axios.get(`${API_URL}/organization/all-org/`, { headers }),
//         Axios.get(`${API_URL}/items/get-all/`, { headers }),
//         Axios.get(`${API_URL}/department/get-all/`, { headers }),
//       ]);

//       return {
//         orgDropDown: Array.isArray(orgRes.data.data)
//           ? orgRes.data.data.map((item: any) => item.organization_name)
//           : [],
//         departmentDropDown: Array.isArray(departmentRes.data.data)
//           ? departmentRes.data.data.map((item: any) => item.department_name)
//           : [],
//         itemsDropDown: Array.isArray(itemsRes.data.data)
//           ? itemsRes.data.data.map((item: any) => item.item_name)
//           : [],
//       };
//     } catch (err) {
//       console.log(err);
//       throw err;
//     }
//   }
// );
